import React, { useEffect, useState } from 'react'
import SmartNavigator from '../generic/SmartNavigator'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import Programs from '../configuration/programs/Programs'
import Workflow from '../configuration/workflow2.0/Workflow'
import ProcessTraceability from '../configuration/processtraceability/ProcessTraceability'
import useFrontEndConfig from '../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { WorkflowHierarchyProvider } from '../configuration/workflow2.0/WorkflowRecursionDetector'
import { useNodesState } from 'reactflow'
import { apiGet } from '../generic/Api_Functions'
import Balanced from '../configuration/balanced/Balanced'
import { useLocation } from 'react-router-dom'

const WorkflowWithProvider = (props) => (
  <WorkflowHierarchyProvider>
    <Workflow {...props} />
  </WorkflowHierarchyProvider>
)

export default function MainWorkflows (props) {
  const activePath = props.activePath
  const { t_ } = useSmartTranslation()
  const [allSubWorkflowDialogs, setAllSubWorkflowDialogs] = useState({})
  const [nodes, setNodes, onNodesChange] = useNodesState([])

  const [frontendConfig] = useFrontEndConfig()
  const [allowEdit, setAllowEdit] = useState(false)
  // S91 (historia 117/HU-05/CA5.4): the BOM library's "Where used" opens a workflow by navigating
  // here with `state: { workflowId }`. Empty object = nothing requested / nothing loaded yet.
  const locationState = useLocation().state
  const requestedWorkflowId = locationState?.workflowId
  // S90 (historia 170/HU-04): the document repository's "Open associated record" for a JOB/TASK/
  // FEATURE_* row carries BOTH `state.workflowId` (above) and this new `state.jobReferenceId`, so the
  // editor can select that job once the workflow loads. Absent (every OTHER caller, S91 de esta
  // historia): the editor receives nothing new to select, unchanged from before.
  const requestedJobReferenceId = locationState?.jobReferenceId
  const [openedWorkflow, setOpenedWorkflow] = useState({})

  useEffect(() => {
    const loadAllowEdit = async () => {
      const allowedIndexValues = await apiGet('indexValues/available')
      setAllowEdit(allowedIndexValues.length > 0)
    }
    loadAllowEdit()
  }, [])

  useEffect(() => {
    if (!requestedWorkflowId) {
      return
    }
    // Same load `WorkflowHeader#loadWorkflow` does when a workflow is picked from the table; the
    // editor mints the fresh `tempWorkflowUUID` and clears the temp document scopes itself when it
    // builds its state from `initialWorkflow`.
    const loadRequestedWorkflow = async () => {
      const response = await apiGet('workflow/' + requestedWorkflowId)
      if (response?.id) {
        setOpenedWorkflow(response)
      }
    }
    loadRequestedWorkflow()
  }, [requestedWorkflowId])

  const componentsInfo = [
    {
      title: t_('Workflows'),
      // S91: `Workflow` seeds its reducer from `initialWorkflow` on mount only, so a workflow
      // opened by id needs an identity of its own to be picked up. `null` is the prop's own
      // "no workflow" value (see `Workflow.jsx`), kept at this boundary.
      component: <WorkflowWithProvider
        key={'workflow-' + (openedWorkflow.id || 'new')}
        initialWorkflow={openedWorkflow.id ? openedWorkflow : null}
        frontendConfig={frontendConfig}
        allSubWorkflowDialogs={allSubWorkflowDialogs}
        setAllSubWorkflowDialogs={setAllSubWorkflowDialogs}
        nodes={nodes}
        setNodes={setNodes}
        onNodesChange={onNodesChange}
        allowEdit={allowEdit}
        showProcessTree
        initialJobReferenceId={requestedJobReferenceId}
                 />,
      to: '/process/workflow2',
      permissionCode: 'WORKFLOWS_TAB'
    },
    {
      title: t_('Programs'),
      component: <Programs />,
      to: '/process/programs',
      permissionCode: 'PROGRAMS_TAB'
    },
    {
      title: t_('Process Traceability'),
      component: <ProcessTraceability />,
      to: '/process/processTraceability',
      permissionCode: 'PROCESS_TRACEABILITY_TAB'
    },
    {
      title: t_('Balanced'),
      component: <Balanced />,
      to: '/process/balanced'
    }
  ]
  return <SmartNavigator componentsInfo={componentsInfo} selected={activePath} sticky={false} />
}
